import React, { useEffect, useState } from 'react';
import { Button, Modal, notification, Spin } from 'antd';
import { TextField } from '@material-ui/core';
import { getInstaUserInfo, updateInstagramInfo, removeInstagramInfo } from '../../api/Insta';
import IconIns from "../../assets/images/ic_instagram.svg";
import { doAction } from 'module-reaction';
import { UpdatePageInfoAction as UpdateStudentPageInfoAction, SocialMediaInputAction as StudentSocialMediaInputAction } from '../../models/model_student_canvas';
import { UpdatePageInfoAction as UpdateBusinessPageInfoAction, SocialMediaInputAction as BusinessSocialMediaInputAction } from '../../models/model_business_canvas';

interface InstagramConnectProps {
  portfolioId: number;    
  isBusiness: boolean;
}

const InstagramConnect: React.FC<InstagramConnectProps> = ({ portfolioId, isBusiness }) => {
  const [instagramHandle, setInstagramHandle] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newHandle, setNewHandle] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!portfolioId || portfolioId === 0) return;

    const fetchInstagramInfo = async () => {
      setLoading(true);
      try {
        const response = await getInstaUserInfo(portfolioId);
        if (response.data?.instagramHandle) {
          setInstagramHandle(response.data.instagramHandle);
        }
      } catch (error) {
        console.error('Error fetching Instagram info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInstagramInfo();
  }, [portfolioId]);

  // Either updates business and school or student and professional
  const updatePageInfo = (newHandle: string) => {
    if (isBusiness) {
      doAction(UpdateBusinessPageInfoAction, {
        instagramHandle: newHandle,
      });
      doAction(BusinessSocialMediaInputAction, {
        label: 'instagram',
        value: newHandle || undefined,
      });
    } else {
      doAction(UpdateStudentPageInfoAction, {
        instagramHandle: newHandle,
      });
      doAction(StudentSocialMediaInputAction, {
        label: 'instagram',
        value: newHandle || undefined,
      });
    }
  }

  const cleanseHandle = (handle: string) => {
    // Remove any characters that are not alphanumeric, dot, or underscore
    let cleansed = handle.replace(/[^a-zA-Z0-9._]/g, '');
    return cleansed;
  }

  const handleConnect = async () => {
    setLoading(true);
    try {
      const cleanHandle = cleanseHandle(newHandle);
      await updateInstagramInfo(portfolioId, cleanHandle);
      setInstagramHandle(cleanHandle);
      updatePageInfo(cleanHandle);
      setIsModalVisible(false);
      setNewHandle('');
    } catch (error) {
      console.error('Error updating Instagram info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      await removeInstagramInfo(portfolioId);
      updatePageInfo('');
      setInstagramHandle('');
    } catch (error) {
      console.error('Error removing Instagram info:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkForPortfolioId = () => {
    if (portfolioId === 0) {
      notification.error({
        message: 'Please save first',
        description: 'Please save first before connecting Instagram',
      });
      return;
    }
    setIsModalVisible(true);
  }

  return (
    <Spin spinning={loading}>
      <div style={{ margin: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img src={IconIns} alt="instagram" style={{ marginRight: '8px' }} />{instagramHandle ? (
            <div>
              <p>Connected Instagram account: @{instagramHandle}</p>
              <Button onClick={handleDisconnect}>
                Disconnect Instagram
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => checkForPortfolioId()}
            >
              Connect to Instagram
            </Button>
          )}
        </div>

        <Modal
          title="Connect Instagram Account"
          visible={isModalVisible}
          onOk={handleConnect}
          onCancel={() => setIsModalVisible(false)}
          okText="Connect"
          confirmLoading={loading}
        >
          <TextField
            label="Instagram Handle"
            variant="filled"
            fullWidth
            value={newHandle}
            onChange={(e) => setNewHandle(e.target.value)}
            placeholder="Enter your Instagram handle"
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default InstagramConnect;
